import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import {
  Droplets, Gauge, Activity, Thermometer, Wind, BatteryCharging,
  Filter as FilterIcon, BellRing, ShieldCheck, LineChart, CircleDot, Server,
  ChevronDown, BarChart3, TrendingUp, LeafyGreen, Cpu,
} from "lucide-react";

const HERO_IMG = "/images/container_water_unit.png";

// ---------------- Data ----------------
const UNIT_FEATURES = [
  { I: Wind,           t: "Atmospheric water generation" },
  { I: FilterIcon,     t: "Air intake & filtration" },
  { I: Droplets,       t: "Condensation & collection" },
  { I: Activity,       t: "Water purification system" },
  { I: Gauge,          t: "Pump and pressure control" },
  { I: Server,         t: "Storage & distribution interface" },
  { I: Cpu,            t: "Control panel" },
  { I: LineChart,      t: "Sensor package" },
  { I: BellRing,       t: "Remote monitoring capability" },
  { I: ShieldCheck,    t: "AuditIQ integration layer" },
];

const MONITORING_CAPS = [
  ["Water production per unit", Droplets],
  ["Total water generated across 9 units", BarChart3],
  ["Humidity level", Wind],
  ["Temperature level", Thermometer],
  ["Dew point", Droplets],
  ["Airflow rate", Wind],
  ["Energy consumption", BatteryCharging],
  ["Pump performance", Gauge],
  ["Filter condition", FilterIcon],
  ["Water quality", Activity],
  ["Storage levels", LineChart],
  ["Maintenance alerts", BellRing],
  ["System uptime", ShieldCheck],
  ["Risk score per unit", CircleDot],
  ["Facility-wide water readiness score", TrendingUp],
];

const STATS = [
  { n: "9 Units",            l: "Per standard deployment" },
  { n: "60,000–90,000+",     l: "Liters/day potential production" },
  { n: "24/7",               l: "Continuous monitoring" },
  { n: "Real-time",          l: "Risk detection" },
  { n: "Predictive",         l: "Maintenance signals" },
  { n: "ESG",                l: "Reporting support" },
  { n: "Cooling",            l: "Continuity support" },
];

const VALUE_ITEMS = [
  "Reduce water supply risk",
  "Support cooling reliability",
  "Improve sustainability reporting",
  "Track performance in real time",
  "Detect failures before downtime",
  "Support emergency water planning",
  "Reduce dependency on stressed municipal systems",
];

const FAQS = [
  { q: "How many water generation units does a data center need?",
    a: "A standard deployment uses 9 containerized water generation units, adjusted for facility size, cooling demand, humidity, and redundancy requirements." },
  { q: "What does AuditIQ monitor?",
    a: "AuditIQ monitors water production, humidity, temperature, dew point, filter status, pump performance, water quality, energy consumption, system uptime, and risk levels across all units." },
  { q: "Can this support data center cooling?",
    a: "The system is designed to support cooling resilience, water availability, emergency reserve planning, and operational continuity." },
  { q: "Is this monitored in real time?",
    a: "Yes. AuditIQ provides 24/7 monitoring, dashboard visibility, risk scoring, and predictive maintenance alerts." },
  { q: "Can investors participate?",
    a: "Yes. Interested investors may submit an inquiry through the investment form on this page." },
];

// Mock dashboard numbers (visual only — branded style)
const mockUnits = Array.from({ length: 9 }, (_, i) => {
  const base = 7500 + ((i * 733) % 2200);
  const eff = 84 + ((i * 11) % 14);
  const status = eff > 94 ? "optimal" : eff > 88 ? "nominal" : "watch";
  return { id: i + 1, liters: base, eff, status };
});
const STATUS_STYLES = {
  optimal: "text-[color:var(--auditiq-success)] border-[color:var(--auditiq-success)]",
  nominal: "text-electric border-electric",
  watch:   "text-[color:var(--auditiq-warning)] border-[color:var(--auditiq-warning)]",
};

// ---------------- Page ----------------
const SolutionsDataCenterWater = () => {
  const [ranges, setRanges] = useState([]);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    organization: "", city: "", state: "",
    investment_interest: "Data Center Water Infrastructure",
    investment_range: "", reason: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    api.get("/investor/meta").then(r => setRanges(r.data.investment_ranges || [])).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.investment_range) {
      return toast.error("Please select an investment range");
    }
    try {
      await api.post("/investor/inquiry", form);
      toast.success("Thank you — we'll be in touch with investor materials.");
      setSubmitted(true);
      setForm({
        first_name: "", last_name: "", email: "", phone: "",
        organization: "", city: "", state: "",
        investment_interest: "Data Center Water Infrastructure",
        investment_range: "", reason: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to submit");
    }
  };

  const totalLiters = mockUnits.reduce((s, u) => s + u.liters, 0);
  const avgEff = Math.round(mockUnits.reduce((s, u) => s + u.eff, 0) / mockUnits.length);
  const scrollToForm = () => {
    document.getElementById("investor-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: "AuditIQ Data Center Water Monitoring",
        applicationCategory: "BusinessApplication",
        description: "Real-time monitoring, risk scoring and ESG reporting for modular water generation units in data center deployments.",
        operatingSystem: "Web",
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      },
      {
        "@type": "Organization",
        name: "AuditIQ",
        url: typeof window !== "undefined" ? window.location.origin : "https://auditiq.ai",
      },
      {
        "@type": "Product",
        name: "Data Center Water Generation Infrastructure",
        description: "9-unit containerized water generation system managed by AuditIQ for data center cooling support and resilience.",
        brand: { "@type": "Brand", name: "AuditIQ" },
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQS.map(f => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <Layout>
      <SEOHead
        title="Data Center Water Generation & Monitoring | AuditIQ"
        description="AuditIQ helps data centers manage modular water generation systems with real-time monitoring, predictive maintenance, ESG reporting, and infrastructure risk intelligence."
        path="/solutions/data-center-water"
        image={HERO_IMG}
        schema={schema}
      />
      <Toaster position="top-right" richColors theme="dark" />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="dc-water-hero">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(45,156,219,0.18),transparent_60%)]" />
        <div className="absolute inset-0 cyber-grid opacity-40" />
        <div className="container-x relative pt-24 pb-20 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-electric">
              <Droplets size={12} /> Data Center Water Infrastructure
            </div>
            <h1 className="font-display text-4xl sm:text-5xl mt-5 leading-[1.05]">
              Data Center Water Generation<br />
              <span className="text-gold">Managed by AuditIQ</span>
            </h1>
            <p className="text-cream-dim text-base lg:text-lg mt-6 max-w-xl">
              AuditIQ helps data centers monitor, manage, and optimize modular water generation
              systems that support cooling reliability, infrastructure resilience, and
              operational continuity.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={scrollToForm} className="btn-gold h-12 px-6 text-sm" data-testid="hero-invest-btn">
                Invest in Data Center Water Infrastructure
              </Button>
              <Link to="/contact">
                <Button className="btn-ghost-gold h-12 px-6 text-sm" data-testid="hero-assessment-btn">
                  Request Data Center Assessment
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-steel">
              <span className="flex items-center gap-2"><Server size={12} /> 9-unit deployment</span>
              <span className="flex items-center gap-2"><Activity size={12} /> 24/7 monitoring</span>
              <span className="flex items-center gap-2"><LeafyGreen size={12} /> ESG reporting</span>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-br from-gold/30 via-electric/20 to-transparent blur-2xl opacity-60 pointer-events-none" />
            <img
              src={HERO_IMG}
              alt="Containerized water generation unit — AuditIQ managed infrastructure"
              className="relative w-full rounded-xl border border-gold-dim/30 shadow-[0_25px_80px_-30px_rgba(0,0,0,0.85)]"
              data-testid="dc-water-hero-image"
            />
            <div className="absolute bottom-3 left-3 glass px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-electric">
              <CircleDot size={10} className="inline mr-1" /> Live monitored
            </div>
          </div>
        </div>
      </section>

      {/* CORE MESSAGE */}
      <section className="bg-navy-deep border-y border-gold-dim/20">
        <div className="container-x py-14">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Purpose-built for modern data centers</div>
            <h2 className="font-display text-3xl md:text-4xl mt-3">
              This system is for data centers that need <span className="text-gold">water resilience</span>.
            </h2>
            <p className="text-cream-dim mt-4">
              AuditIQ acts as the intelligence and governance layer that monitors each unit, analyzes
              performance, detects risk, and generates reports — so your facility can run continuously,
              sustainably, and independently of stressed municipal water systems.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-3 mt-8">
            {[
              "Independent water generation",
              "Cooling system support",
              "Water-resilience infrastructure",
              "Reduced municipal water dependency",
              "Predictive infrastructure monitoring",
              "ESG & sustainability reporting",
              "Operational risk reduction",
            ].map((t, i) => (
              <div key={i} className="glass px-4 py-3 text-sm text-cream flex items-start gap-3">
                <ShieldCheck size={14} className="text-gold mt-0.5 shrink-0" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9-UNIT DEPLOYMENT */}
      <section className="py-16">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Deployment Scale</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3">Standard Data Center Deployment</h2>
              <p className="text-cream-dim mt-3 max-w-2xl">
                A standard data center deployment requires <span className="text-gold font-semibold">9 containerized water generation units</span> working
                together as a coordinated system for production, cooling support, redundancy, and maintenance rotation.
              </p>
            </div>
            <div className="glass p-5 text-center" data-testid="dc-water-units-required">
              <div className="text-[11px] uppercase tracking-[0.22em] text-steel">Units required per data center</div>
              <div className="font-display text-5xl text-gold mt-1">9 Units</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-electric mt-1">Coordinated system</div>
            </div>
          </div>

          {/* 9-unit visual grid */}
          <div className="grid grid-cols-3 gap-4" data-testid="dc-water-unit-grid">
            {mockUnits.map(u => (
              <article key={u.id} className="glass p-4 hover:border-gold transition-colors">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Unit</div>
                  <span className={`text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 rounded border ${STATUS_STYLES[u.status]}`}>
                    {u.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <div className="w-10 h-10 rounded bg-navy-deep border border-gold-dim/30 flex items-center justify-center">
                    <Droplets size={16} className="text-electric" />
                  </div>
                  <div>
                    <div className="font-display text-2xl text-gold leading-none">#{u.id}</div>
                    <div className="text-[10px] uppercase tracking-wider text-steel mt-1">Container</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-steel">Production</div>
                    <div className="text-cream font-mono">{u.liters.toLocaleString()} L/day</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-steel">Efficiency</div>
                    <div className="text-cream font-mono">{u.eff}%</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* UNIT FUNCTIONALITY */}
      <section className="bg-navy-deep border-y border-gold-dim/20">
        <div className="container-x py-16">
          <div className="grid lg:grid-cols-[1fr_1.15fr] gap-10 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Inside each unit</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3">Containerized unit components</h2>
              <p className="text-cream-dim mt-3 max-w-lg">
                Every AuditIQ-managed unit is an end-to-end water generation stack, ready for
                drop-in deployment at any qualifying data center facility.
              </p>
              <img src={HERO_IMG} alt="Interior of a containerized water generation unit"
                className="mt-6 rounded-lg border border-gold-dim/25 w-full" />
            </div>
            <ul className="grid sm:grid-cols-2 gap-3">
              {UNIT_FEATURES.map(({ I, t }, i) => (
                <li key={i} className="glass p-4 flex items-start gap-3" data-testid={`dc-water-unit-feat-${i}`}>
                  <div className="w-9 h-9 rounded bg-navy-deep border border-gold-dim/30 flex items-center justify-center shrink-0">
                    <I size={14} className="text-gold" />
                  </div>
                  <span className="text-cream text-sm">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* AUDITIQ MANAGEMENT CAPABILITIES */}
      <section className="py-16">
        <div className="container-x">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">AuditIQ Management</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3">What AuditIQ monitors</h2>
          <p className="text-cream-dim mt-3 max-w-2xl">15 live data streams per unit, rolled up across the facility.</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3 mt-8">
            {MONITORING_CAPS.map(([t, I], i) => (
              <div key={i} className="glass px-4 py-3 flex items-start gap-3" data-testid={`dc-water-cap-${i}`}>
                <I size={14} className="text-electric mt-0.5 shrink-0" />
                <span className="text-cream text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DASHBOARD PREVIEW */}
      <section className="bg-navy-deep border-y border-gold-dim/20">
        <div className="container-x py-16">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Dashboard Preview</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3">AuditIQ Data Center Water Dashboard</h2>
            </div>
            <Link to="/demo">
              <Button className="btn-ghost-gold h-10 px-4 text-xs" data-testid="dc-water-open-demo">
                Open Live Demo Mode →
              </Button>
            </Link>
          </div>

          {/* Top KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { l: "Total water today",     v: `${totalLiters.toLocaleString()} L`, s: BarChart3, c: "text-gold" },
              { l: "System efficiency",      v: `${avgEff}%`,                       s: Gauge,     c: "text-electric" },
              { l: "Cooling water avail.",   v: "READY",                            s: Droplets,  c: "text-[color:var(--auditiq-success)]" },
              { l: "Units online",           v: "9 / 9",                            s: Server,    c: "text-gold" },
            ].map((k, i) => (
              <div key={i} className="glass p-4" data-testid={`dc-water-kpi-${i}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-steel">{k.l}</div>
                  <k.s size={14} className={k.c} />
                </div>
                <div className={`font-display text-2xl mt-2 ${k.c}`}>{k.v}</div>
              </div>
            ))}
          </div>

          {/* Second row */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            <div className="glass p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Energy consumption</div>
              <div className="font-display text-xl text-cream mt-1">124.3 kWh <span className="text-xs text-steel">/hr</span></div>
              <div className="h-1.5 bg-navy-deep rounded mt-3 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-electric to-gold" style={{ width: "68%" }} />
              </div>
            </div>
            <div className="glass p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Water quality status</div>
              <div className="font-display text-xl text-[color:var(--auditiq-success)] mt-1">Potable — pass</div>
              <div className="text-[10px] text-cream-dim mt-2">pH 7.2 · TDS 38 ppm · turbidity 0.3 NTU</div>
            </div>
            <div className="glass p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Maintenance alerts</div>
              <div className="font-display text-xl text-gold mt-1">2 open</div>
              <div className="text-[10px] text-cream-dim mt-2">Unit #4 filter 82% · Unit #7 pressure drift</div>
            </div>
          </div>

          {/* Per-unit risk row */}
          <div className="glass p-4 mt-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Unit Risk Scores</div>
              <span className="text-[10px] uppercase tracking-[0.22em] text-electric">Lower = safer</span>
            </div>
            <div className="grid grid-cols-9 gap-2 mt-3">
              {mockUnits.map(u => {
                const risk = 100 - u.eff;
                const c = risk < 10 ? "bg-[color:var(--auditiq-success)]"
                        : risk < 14 ? "bg-electric"
                        : "bg-[color:var(--auditiq-warning)]";
                return (
                  <div key={u.id} className="flex flex-col items-center gap-1">
                    <div className="w-full h-20 bg-navy-deep rounded flex items-end">
                      <div className={`w-full ${c} rounded-b transition-all`} style={{ height: `${Math.min(95, risk * 4)}%` }} />
                    </div>
                    <div className="text-[10px] text-cream-dim">#{u.id}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ESG + Investment row */}
          <div className="grid md:grid-cols-2 gap-3 mt-3">
            <div className="glass p-4 flex items-center gap-4">
              <LeafyGreen size={28} className="text-[color:var(--auditiq-success)]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-steel">ESG reporting status</div>
                <div className="text-cream mt-1">On track · ready for Q report</div>
              </div>
            </div>
            <div className="glass p-4 flex items-center gap-4">
              <TrendingUp size={28} className="text-gold" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-steel">Investment performance</div>
                <div className="text-cream mt-1">Uptime 99.6% · asset utilization 91%</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATISTICS */}
      <section className="py-16">
        <div className="container-x">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {STATS.map((s, i) => (
              <div key={i} className="glass p-4 text-center" data-testid={`dc-water-stat-${i}`}>
                <div className="font-display text-xl text-gold">{s.n}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-cream-dim mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPERATIONAL VALUE */}
      <section className="bg-navy-deep border-y border-gold-dim/20">
        <div className="container-x py-16">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Operational value</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3">Why data centers deploy this system</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
            {VALUE_ITEMS.map((t, i) => (
              <div key={i} className="glass p-5 flex items-start gap-3">
                <div className="w-9 h-9 rounded bg-navy-soft border border-gold-dim/25 flex items-center justify-center shrink-0">
                  <ShieldCheck size={14} className="text-gold" />
                </div>
                <span className="text-cream">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTMENT */}
      <section id="investor-form" className="py-16">
        <div className="container-x">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10">
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Infrastructure Investment</div>
              <h2 className="font-display text-3xl md:text-4xl mt-3">
                Invest in Data Center<br />
                <span className="text-gold">Water Infrastructure</span>
              </h2>
              <p className="text-cream-dim mt-4 max-w-lg">
                AuditIQ is developing an infrastructure intelligence model that combines water
                generation, monitoring, risk management, and data center resilience. Investors
                can participate in infrastructure expansion, deployment partnerships, and
                monitored water generation systems designed for AI-era data center demand.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-cream">
                {[
                  "9-unit deployment per data center",
                  "Monitored revenue-generating infrastructure",
                  "Recurring monitoring & ESG reporting contracts",
                  "Resilience assets aligned with AI-era compute demand",
                ].map((t, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <CircleDot size={10} className="text-gold" /> {t}
                  </li>
                ))}
              </ul>
            </div>

            {/* FORM */}
            {submitted ? (
              <div className="glass p-8 text-center" data-testid="investor-form-thankyou">
                <ShieldCheck size={28} className="text-gold mx-auto" />
                <h3 className="font-display text-2xl mt-4">Thank you.</h3>
                <p className="text-cream-dim mt-2">An AuditIQ team member will follow up with investor materials.</p>
                <Button onClick={() => setSubmitted(false)} className="btn-ghost-gold h-10 px-4 text-xs mt-6">
                  Submit another inquiry
                </Button>
              </div>
            ) : (
            <form onSubmit={submit} className="glass p-6 space-y-3" data-testid="investor-form">
              <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Investor Inquiry</div>
              <h3 className="font-display text-2xl">Request Investment Information</h3>
              <div className="grid md:grid-cols-2 gap-3 pt-2">
                <Input required placeholder="First Name" value={form.first_name} onChange={set("first_name")}
                  className="bg-navy-deep border-gold-dim/40" data-testid="inv-first-name" />
                <Input required placeholder="Last Name" value={form.last_name} onChange={set("last_name")}
                  className="bg-navy-deep border-gold-dim/40" data-testid="inv-last-name" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Input required type="email" placeholder="Email Address" value={form.email} onChange={set("email")}
                  className="bg-navy-deep border-gold-dim/40" data-testid="inv-email" />
                <Input placeholder="Phone Number" value={form.phone} onChange={set("phone")}
                  className="bg-navy-deep border-gold-dim/40" data-testid="inv-phone" />
              </div>
              <Input placeholder="Organization Name" value={form.organization} onChange={set("organization")}
                className="bg-navy-deep border-gold-dim/40" data-testid="inv-org" />
              <div className="grid md:grid-cols-2 gap-3">
                <Input placeholder="City" value={form.city} onChange={set("city")}
                  className="bg-navy-deep border-gold-dim/40" data-testid="inv-city" />
                <Input placeholder="State" value={form.state} onChange={set("state")}
                  className="bg-navy-deep border-gold-dim/40" data-testid="inv-state" />
              </div>
              <Input placeholder="Investment Interest" value={form.investment_interest} onChange={set("investment_interest")}
                className="bg-navy-deep border-gold-dim/40" data-testid="inv-interest" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-steel">Available Investment Range *</label>
                <Select value={form.investment_range} onValueChange={set("investment_range")}>
                  <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream mt-1" data-testid="inv-range">
                    <SelectValue placeholder="Select a range" />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
                    {ranges.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Textarea rows={4} placeholder="Why do you want to invest in this infrastructure?"
                value={form.reason} onChange={set("reason")}
                className="bg-navy-deep border-gold-dim/40 text-cream" data-testid="inv-reason" />
              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <Button type="submit" className="btn-gold h-12 px-6 flex-1" data-testid="inv-submit">
                  Request Investment Information
                </Button>
                <Button type="button" onClick={() => setForm({
                  first_name: "", last_name: "", email: "", phone: "",
                  organization: "", city: "", state: "",
                  investment_interest: "Data Center Water Infrastructure",
                  investment_range: "", reason: "",
                })} className="btn-ghost-gold h-12 px-4 text-xs">
                  Clear
                </Button>
              </div>
              <p className="text-[10px] text-cream-dim pt-2">
                By submitting, you agree to be contacted by AuditIQ regarding this infrastructure investment opportunity.
              </p>
            </form>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-navy-deep border-t border-gold-dim/20">
        <div className="container-x py-16 max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.28em] text-electric">Frequently asked</div>
          <h2 className="font-display text-3xl md:text-4xl mt-3">Data center water FAQ</h2>
          <div className="mt-8 space-y-2" data-testid="dc-water-faq">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div key={i} className="glass overflow-hidden">
                  <button onClick={() => setOpenFaq(open ? -1 : i)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                    data-testid={`faq-toggle-${i}`}>
                    <span className="text-cream">{f.q}</span>
                    <ChevronDown size={16} className={`text-gold transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="px-5 pb-5 text-cream-dim text-sm">{f.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-16">
        <div className="container-x">
          <div className="glass p-8 flex items-center justify-between flex-wrap gap-4">
            <div>
              <h3 className="font-display text-2xl">Ready to secure water resilience for your facility?</h3>
              <p className="text-cream-dim text-sm mt-2">Request a data center assessment or start an investor conversation today.</p>
            </div>
            <div className="flex gap-3">
              <Link to="/contact"><Button className="btn-ghost-gold h-11 px-5 text-xs">Request Assessment</Button></Link>
              <Button onClick={scrollToForm} className="btn-gold h-11 px-5 text-xs">Investor Inquiry</Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SolutionsDataCenterWater;
